import { useState } from 'react'
import { useAuthStore, updatePassword, reauthenticateWithPassword } from '@lipaflow/shared'
import { useMutation } from '@tanstack/react-query'
import { Badge, PageHeader } from '@lipaflow/ui'
import { usePageTitle } from '../lib/pageTitle'
import { PlatformHealth } from '../components/PlatformHealth'
import { usePlatformInfo } from '../lib/platformInfo'
import toast from 'react-hot-toast'
import {
  UserCircleIcon,
  ServerStackIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'

const tabs = [
  { id: 'account', label: 'Account', icon: UserCircleIcon },
  { id: 'platform', label: 'Platform', icon: ServerStackIcon },
] as const

type TabId = (typeof tabs)[number]['id']

export default function SettingsPage() {
  usePageTitle('Your account')
  const [activeTab, setActiveTab] = useState<TabId>('account')

  return (
    <div className="space-y-6">
      <PageHeader title="Your account" subtitle="Your profile and password, and the health of the platform." />

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 rounded-xl p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-inter text-body-sm transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-white text-stone-800 font-medium shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'account' && <AccountTab />}
      {activeTab === 'platform' && <PlatformTab />}
    </div>
  )
}

function AccountTab() {
  const user = useAuthStore((state) => state.user)

  return (
    <div className="space-y-6">
      {/* Profile Info */}
      <div className="bg-white rounded-2xl shadow-card border border-stone-100 p-6">
        <h3 className="font-outfit text-h4 text-stone-800 mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="font-inter text-caption text-stone-400 mb-1">Full Name</p>
            <p className="font-inter text-body text-stone-800">
              {user?.first_name} {user?.last_name}
            </p>
          </div>
          <div>
            <p className="font-inter text-caption text-stone-400 mb-1">Email</p>
            <p className="font-inter text-body text-stone-800">{user?.email ?? '-'}</p>
          </div>
          <div>
            <p className="font-inter text-caption text-stone-400 mb-1">Role</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 capitalize">
              {user?.role?.replace('_', ' ') ?? '-'}
            </span>
          </div>
          <div>
            <p className="font-inter text-caption text-stone-400 mb-1">Phone</p>
            <p className="font-inter text-body text-stone-800">{user?.phone ?? 'Not set'}</p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <ChangePasswordForm />
    </div>
  )
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const user = useAuthStore((state) => state.user)

  const mutation = useMutation({
    mutationFn: async () => {
      // Re-authenticate first (Firebase requires recent login for password change)
      if (user?.email) {
        await reauthenticateWithPassword(user.email, currentPassword)
      }
      await updatePassword(newPassword)
    },
    onSuccess: () => {
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    },
    onError: (err: unknown) => {
      const firebaseErr = err as { code?: string }
      if (firebaseErr.code === 'auth/wrong-password' || firebaseErr.code === 'auth/invalid-credential') {
        toast.error('Current password is incorrect')
      } else if (firebaseErr.code === 'auth/weak-password') {
        toast.error('New password is too weak. Use at least 6 characters.')
      } else {
        toast.error('Failed to change password. Please try again.')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="bg-white rounded-2xl shadow-card border border-stone-100 p-6">
      <h3 className="font-outfit text-h4 text-stone-800 mb-4">Change Password</h3>
      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        <div>
          <label className="label">Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600"
            >
              {showCurrent ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="label">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input pr-10"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600"
            >
              {showNew ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="label">Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input"
            required
            minLength={8}
          />
        </div>

        <button
          type="submit"
          disabled={mutation.isPending || !currentPassword || !newPassword || !confirmPassword}
          className="btn-primary"
        >
          {mutation.isPending ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  )
}

function PlatformTab() {
  // From the running API, not the build: a deployed build always reports
  // "production" mode, even on staging, and the version was hard-coded.
  const info = usePlatformInfo()
  const apiUrl = import.meta.env.VITE_API_URL || 'Not configured'

  return (
    <div className="space-y-6">
      <div className="bg-surface rounded-card border border-hairline p-6">
        <h3 className="font-outfit text-h4 text-ink mb-4">Platform information</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-caption text-ink-faint mb-1">API version</dt>
            <dd className="text-body text-ink font-mono">{info.data?.version ?? (info.isError ? 'Unavailable' : '…')}</dd>
          </div>
          <div>
            <dt className="text-caption text-ink-faint mb-1">Environment</dt>
            <dd>
              {info.data ? (
                <Badge variant={info.data.environment === 'production' ? 'success' : 'warning'} size="sm">{info.data.environment}</Badge>
              ) : (
                <span className="text-body-sm text-ink-muted">{info.isError ? 'Unavailable' : '…'}</span>
              )}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-caption text-ink-faint mb-1">API address</dt>
            <dd className="font-mono text-body-sm text-ink bg-surface-alt rounded-button px-3 py-2 break-all">{apiUrl}</dd>
          </div>
        </dl>
      </div>

      <PlatformHealth />
    </div>
  )
}
